import { useState, useEffect } from 'react';
import { SaudiRiyal } from 'lucide-react';
import BadgeDesignModal from './BadgeDesignModal';
import TicketEditModal from './TicketEditModal';
import TicketCreateModal from './TicketCreateModal';
import { useAppState } from '../contexts/AppStateContext';
import api from '../utils/axiosConfig';

const TicketManagement = () => {
    const { currentEvent } = useAppState();
    const [tickets, setTickets] = useState([]);
    const [designModalOpen, setDesignModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchTickets = async () => {
            if (!currentEvent || !currentEvent.id) return;
            
            setLoading(true);
            setError(null);
            
            try {
                const response = await api.get(`/events/${currentEvent.id}/tickets/`);
                setTickets(response.data);
                setLoading(false);                
            } catch (error) {
                console.error('Error fetching tickets:', error);
                setError('Failed to load tickets. Please try again.');
                setLoading(false);
            }
        };
        
        fetchTickets();
    }, [currentEvent]);

    // Open design modal
    const handleOpenDesignModal = (ticket) => {
        setSelectedTicket({...ticket}); // Create a copy to avoid reference issues
        setDesignModalOpen(true);
    };

    // Open edit modal
    const handleOpenEditModal = (ticket) => {
        setSelectedTicket({...ticket}); // Create a copy to avoid reference issues
        setEditModalOpen(true);
    };

    // Open create modal
    const handleCreateTicketType = () => {
        setCreateModalOpen(true);
    };

    const handleSaveDesign = async (updatedTicket) => {
        try {
            await api.put(`/events/${currentEvent.id}/tickets/${updatedTicket.id}/`, {
                badge_design: updatedTicket.badge_design
            });
            
            setTickets(tickets.map(ticket =>
                ticket.id === updatedTicket.id ? {...ticket, badge_design: updatedTicket.badge_design} : ticket
            ));
            
        } catch (error) {
            alert('Failed to save badge design. Please try again.');
        }
    };

    const handleSaveTicket = async (formData, ticketId) => {
        try {
            const response = await api.put(
                `/events/${currentEvent.id}/tickets/${ticketId}/`, 
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            
            setTickets(tickets.map(ticket =>
                ticket.id === ticketId ? response.data : ticket
            ));
            
            alert('Ticket updated successfully!');
        } catch (error) {
            console.error('Error updating ticket:', error);
            alert('Failed to update ticket. Please try again.');
        }
    };

    // Create new ticket
    const handleCreateTicket = async (formData) => {
        try {
            // Create API call
            const response = await api.post(
                `/events/${currentEvent.id}/tickets/`, 
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            
            // Add new ticket to local state
            setTickets([...tickets, response.data]);
            
            alert('New ticket created successfully!');
        } catch (error) {
            console.error('Error creating ticket:', error);
            alert('Failed to create ticket. Please try again.');
        }
    };

    // Delete ticket
    const handleDeleteTicket = async (ticketId) => {
        if (window.confirm('Are you sure you want to delete this ticket?')) {
            try {
                // Delete API call
                await api.delete(`/events/${currentEvent.id}/tickets/${ticketId}/`);
                
                // Update local state
                setTickets(tickets.filter(ticket => ticket.id !== ticketId));
                
                alert('Ticket deleted successfully!');
            } catch (error) {
                console.error('Error deleting ticket:', error);
                alert('Failed to delete ticket. Please try again.');
            }
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    if (loading) {
        return <div className="p-6 text-center">Loading tickets...</div>;
    }

    if (error) {
        return <div className="p-6 text-center text-red-500">{error}</div>;
    }

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-xl font-bold">Ticket Management</h1>
                <button
                    type="button"
                    onClick={handleCreateTicketType}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    Add New Ticket Type
                </button>
            </div>

            {tickets.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <p className="text-gray-500">No tickets found for this event.</p>
                    <button
                        type="button"
                        onClick={handleCreateTicketType}
                        className="mt-4 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Create Your First Ticket Type
                    </button>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Ticket
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Price
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Quantity
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Custom Text
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Badge
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {tickets.map((ticket) => (
                                <tr key={ticket.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="font-medium text-gray-900">{ticket.name}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-gray-900 flex items-center gap-1">
                                            <SaudiRiyal className="h-3.5 w-3.5" aria-label="SAR" />
                                            {formatCurrency(ticket.price)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-gray-900">{ticket.quantity}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-gray-900 max-w-xs truncate">{ticket.custom_text}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {ticket.badge_image ? (
                                            <img
                                                src={ticket.badge_image}
                                                alt={`${ticket.name} badge`}
                                                className="h-10 w-auto object-contain"
                                            />
                                        ) : (
                                            <span className="text-gray-500 text-sm">No badge image</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => handleOpenEditModal(ticket)}
                                                className="text-indigo-600 hover:text-indigo-900"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleOpenDesignModal(ticket)}
                                                className="text-green-600 hover:text-green-900"
                                            >
                                                Badge Design
                                            </button>
                                            <button
                                                onClick={() => handleDeleteTicket(ticket.id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Badge Design Modal */}
            {selectedTicket && (
                <BadgeDesignModal
                    ticket={selectedTicket}
                    isOpen={designModalOpen}
                    onClose={() => {
                        setDesignModalOpen(false);
                        setSelectedTicket(null);
                    }}
                    onSave={handleSaveDesign}
                />
            )}

            {/* Ticket Edit Modal */}
            {selectedTicket && (
                <TicketEditModal
                    ticket={selectedTicket}
                    isOpen={editModalOpen}
                    onClose={() => {
                        setEditModalOpen(false);
                        setSelectedTicket(null);
                    }}
                    onSave={handleSaveTicket}
                />
            )}

            {/* Ticket Create Modal */}
            <TicketCreateModal
                isOpen={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onSave={handleCreateTicket}
            />
        </div>
    );
};

export default TicketManagement;